import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  TextInput,
  Button,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  Dropdown,
  Loading
} from 'carbon-components-react';
import { FormError } from 'components';
import { APP_URL, CFCProps, CHANNELS, DraftChannelDataType } from 'types';
import { RootState } from 'redux/root.reducer';
import { selectDraftChannels, selectGmlProjectionItems } from 'redux/channels';
import { Launch16 } from '@carbon/icons-react';
import { setSandBoxTestingMode } from 'redux/lodgeEnquiry';
import { useHistory } from 'react-router-dom';
import AdvancedFeaturesTile from '../advancedFeatures';
import PrimaryContentForm from '../primaryContent';

const EmailChannelForm: React.FC<Props | any> = (props: any) => {
  const params = new URLSearchParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const draftChannel: DraftChannelDataType = useSelector(selectDraftChannels);
  const gmlProjectionOptions: any = useSelector(selectGmlProjectionItems);
  const [isRequiredAttachment, setIsRequiredAttachment] = useState<boolean>(
    false
  );
  const [
    gmlProjectionSelected,
    setGmlProjectionSelected
  ] = useState<Object | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | undefined>();
  const [isFormFieldsDirty, setIsFormFieldsDirty] = useState<boolean>(false);
  const [
    isAdvancedOptionsInvavid,
    setIsAdvancedOptionsInvavid
  ] = useState<boolean>(false);
  const [isAdvancedOptionsDirty, setIsAdvancedOptionsDirty] = useState<boolean>(
    false
  );
  const [isPrimaryContentInvavid, setPrimaryContentInvalid] = useState<boolean>(
    false
  );
  const [isPrimaryContentIsDirty, setIsPrimaryContentDirty] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (draftChannel) {
      setIsLoading(draftChannel.submitting);
    }
  }, [draftChannel]);

  useEffect(() => {
    if (isAdvancedOptionsDirty) {
      setIsFormFieldsDirty(true);
    }
  }, [isAdvancedOptionsDirty]);

  const {
    advancedOptions,
    primaryContentOptions,
    values,
    errors: fErrors,
    touched: fTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit
  } = props;
  const inputError = ((errors: any, touched: any) => (
    key:
      | 'referralSummaryEmail'
      | 'requireAttachment'
      | 'gmlProjection'
      | 'isGifRequired'
      | 'isXmlRequired'
      | 'isKmlRequired'
      | 'isGmlRequired'
      | 'isPdfRequired'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );

  const changeHandler = (e: any) => {
    if (e.target.type === 'checkbox') {
      setFieldValue(e.target.id, e.target.checked);
      if (e.target.id === 'isGmlRequired') {
        if (e.target.checked) {
          if (!values.gmlVersion) {
            setFieldValue('gmlVersion', 'GMLv3');
          }
        } else {
          setFieldValue('gmlProjection', '');
          setFieldValue('gmlVersion', '');
        }
      }
      setIsRequiredAttachment(!e.target.checked);
    }
  };
  const gmlProjectionItems: [] =
    gmlProjectionOptions &&
    gmlProjectionOptions.map((item: any) => ({
      label: item.label === '-' ? 'Select...' : item.label,
      value: item.code
    }));
  const _isAttachmentFieldTouched =
    fTouched['isGifRequired'] ||
    fTouched['isGmlRequired'] ||
    fTouched['isXmlRequired'] ||
    fTouched['isKmlRequired'] ||
    fTouched['isPdfRequired'];
  const _isRequiredAttachment = !(
    values.isGifRequired ||
    values.isGmlRequired ||
    values.isXmlRequired ||
    values.isKmlRequired ||
    values.isPdfRequired
  );
  const _isFormFieldsDirty = !!(
    fTouched['referralSummaryEmail'] || _isAttachmentFieldTouched
  );

  useEffect(() => {
    if (values) {
      const { gmlProjection } = values;
      if (gmlProjection) {
        const gmlVal = gmlProjectionOptions?.filter(
          (item: any) => item.code === gmlProjection
        )[0];
        gmlVal && setGmlProjectionSelected(gmlVal);
      } else {
        setGmlProjectionSelected(null);
      }
      // SET LOCAL STATE
      setIsFormFieldsDirty(true);
      setIsRequiredAttachment(_isRequiredAttachment);
    }
  }, [_isRequiredAttachment, values, gmlProjectionOptions]);

  const gmlProjectionHandler = (val: any) => {
    setFieldValue('gmlProjection', val.selectedItem.value);
    setIsFormFieldsDirty(true);
  };
  useEffect(() => {
    setIsFormFieldsDirty(_isFormFieldsDirty);
  }, [_isFormFieldsDirty]);

  const sandBoxTestHandler = () => {
    dispatch(setSandBoxTestingMode({ isSandboxTest: true }));
    params.append('isSandboxTest', 'true');
    history.push({
      pathname: APP_URL.LODGE_NEW_ENQUIRY,
      search: '?' + params.toString()
    });
  };

  return (
    <React.Fragment>
      <div className="form-mid--body">
        <div className="form-section--item">
          <TextInput
            id="referralSummaryEmail"
            name="referralSummaryEmail"
            labelText="Email Address"
            helperText="One or more comma-separated email addresses"
            onChange={handleChange}
            onBlur={handleBlur}
            defaultValue={values.referralSummaryEmail}
            data-testid="referralSummaryEmail"
          />
          {inputError('referralSummaryEmail')}
        </div>
        <hr className="section__divider" />
        <FormGroup
          className="attachments"
          legendText="Required attachments"
          onChange={(e) => changeHandler(e)}
        >
          <div className="checkbox-fromgroup">
            <Checkbox
              labelText="GIF"
              value="GIF"
              onBlur={handleBlur}
              defaultChecked={values.isGifRequired}
              id="isGifRequired"
            />
            <Checkbox
              labelText="GML"
              value="GML"
              onBlur={handleBlur}
              defaultChecked={values.isGmlRequired}
              id="isGmlRequired"
            />
            <Checkbox
              labelText="XML"
              value="XML"
              onBlur={handleBlur}
              defaultChecked={values.isXmlRequired}
              id="isXmlRequired"
            />
            <Checkbox
              labelText="KML"
              value="KML"
              onBlur={handleBlur}
              defaultChecked={values.isKmlRequired}
              id="isKmlRequired"
            />
            <Checkbox
              labelText="PDF"
              value="PDF"
              onBlur={handleBlur}
              defaultChecked={values.isPdfRequired}
              id="isPdfRequired"
            />
          </div>
          {isRequiredAttachment && _isAttachmentFieldTouched && (
            <FormError>{fErrors.requireAttachment}</FormError>
          )}
        </FormGroup>
        {values.isGmlRequired && gmlProjectionItems?.length > 0 && (
          <>
            <FormGroup className="gml-version" legendText="GML version">
              <RadioButtonGroup
                defaultSelected={values.gmlVersion}
                name="gml-version-radio-button-group"
                onChange={(value: string) => setFieldValue('gmlVersion', value)}
              >
                <RadioButton
                  id="gml-version-radio-1"
                  labelText="GML v2"
                  value="GMLv2"
                />
                <RadioButton
                  id="gml-version-radio-2"
                  labelText="GML v3"
                  value="GMLv3"
                />
              </RadioButtonGroup>
            </FormGroup>
            <div className="form-section--item">
              <Dropdown
                ariaLabel="Dropdown gml Projection"
                id="gmlProjection"
                items={gmlProjectionItems}
                label="Select..."
                titleText="GML Projection"
                initialSelectedItem={gmlProjectionSelected}
                selectedItem={gmlProjectionSelected}
                onChange={(e) => gmlProjectionHandler(e)}
              />
              {inputError('gmlProjection')}
            </div>
          </>
        )}
      </div>
      <hr className="section__divider" />
      <PrimaryContentForm
        primaryContentOptions={primaryContentOptions}
        setPrimaryContentInvalid={(flag: boolean) =>
          setPrimaryContentInvalid(flag)
        }
        setPrimaryContentDirty={(flag: boolean) =>
          setIsPrimaryContentDirty(flag)
        }
      />
      <AdvancedFeaturesTile
        advancedOptions={advancedOptions}
        setAdvancedOptionsInvavid={(flag: boolean) =>
          setIsAdvancedOptionsInvavid(flag)
        }
        setAdvancedOptionsDirty={(flag: boolean) =>
          setIsAdvancedOptionsDirty(flag)
        }
      />
      <hr className="section__divider" />
      <div className="form-button--wrapper">
        <Button
          className="reg-submit"
          type="submit"
          kind={'secondary'}
          data-testid="submit"
          size="field"
          onClick={() => {
            values.actionType = 'save-draft';
            handleSubmit(values);
          }}
          disabled={
            (draftChannel && draftChannel.configurationType === CHANNELS.LEGACY
              ? !isFormFieldsDirty && !isPrimaryContentIsDirty
              : false) ||
            isLoading ||
            isAdvancedOptionsInvavid ||
            isPrimaryContentInvavid
          }
        >
          {'Save draft'}
        </Button>
        <Button
          renderIcon={Launch16}
          kind="tertiary"
          size="field"
          disabled={
            !(
              draftChannel &&
              draftChannel.configurationType === CHANNELS.LEGACY &&
              !isFormFieldsDirty &&
              !isPrimaryContentIsDirty
            ) ||
            isLoading ||
            isAdvancedOptionsInvavid ||
            isPrimaryContentInvavid
          }
          onClick={() => sandBoxTestHandler()}
        >
          Test run
        </Button>
        <Button
          className="submit-active"
          type="submit"
          kind={'primary'}
          size="field"
          data-testid="submit-active"
          onClick={() => {
            values.actionType = 'make-active';
            handleSubmit(values);
          }}
          disabled={
            !(
              draftChannel &&
              draftChannel.configurationType === CHANNELS.LEGACY &&
              !isFormFieldsDirty &&
              !isPrimaryContentIsDirty
            ) ||
            isLoading ||
            isAdvancedOptionsInvavid ||
            isPrimaryContentInvavid
          }
        >
          {'Make Active'}
        </Button>
        {isLoading && (
          <Loading
            small
            description="Active loading indicator"
            withOverlay={false}
          />
        )}
      </div>
      <p className="footer--content">
        Save your draft and use ‘Test run’ to lodge a mock enquiry in Sandbox
        testing mode
      </p>
    </React.Fragment>
  );
};

const mapStateToProps = ({ referralChannels }: RootState) => ({
  referralChannels
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

EmailChannelForm.displayName = 'EmailChannelForm';

export default connector(EmailChannelForm);
